<template>
    <div class="container-nzidoo">
        <page-title title="sidebar.boosterPurchasesManagement" />

        <!-- Tabs for filtering by type -->
        <div class="tabs">
            <button class="btn-tab" :class="{ active: activeTab === 'all' }" @click="activeTab = 'all'">
                {{ $t('filter.all') }}
            </button>
            <button class="btn-tab" :class="{ active: activeTab === 'boost_ad' }" @click="activeTab = 'boost_ad'">
                {{ $t('boosterPlans.type.boost_ad') }}
            </button>
            <button class="btn-tab" :class="{ active: activeTab === 'top_visibility' }"
                @click="activeTab = 'top_visibility'">
                {{ $t('boosterPlans.type.top_visibility') }}
            </button>
            <button class="btn-tab" :class="{ active: activeTab === 'urgent_ad' }" @click="activeTab = 'urgent_ad'">
                {{ $t('boosterPlans.type.urgent_ad') }}
            </button>
        </div>

        <LoadingSpinner v-if="loader" />
        
        <!-- Toolbar -->
        <div class="admin-toolbar">
            <div class="admin-search-box">
                <i class="mdi mdi-magnify admin-search-icon"></i>
                <input type="text" v-model="searchTerm" :placeholder="$t('sentence.searchByName')"
                    class="admin-search-input">
                <i v-if="searchTerm" class="mdi mdi-close-circle admin-clear-icon" @click="clearSearch"
                    :title="$t('sentence.clear')"></i>
            </div>

            <!-- Filter by status -->
            <select v-model="filterStatus" class="admin-form-input" style="width: auto; min-width: 150px;">
                <option value="">{{ $t('filter.all') }}</option>
                <option value="pending">{{ $t('filter.pending') }}</option>
                <option value="inProgress">{{ $t('filter.inProgress') }}</option>
                <option value="completed">{{ $t('filter.completed') }}</option>
                <option value="cancelled">{{ $t('filter.cancelled') }}</option>
                <option value="expired">{{ $t('filter.expired') }}</option>
            </select>
        </div>

        <!-- Booster Purchases List -->
        <div v-if="filteredPurchases && filteredPurchases.length" class="products-section">
            <div class="products-container">
                <div class="table-responsive">
                    <table class="table table-hover mb-0">
                        <thead class="thead-light">
                            <tr>
                                <th class="border-top-0">{{ $t('attr.product') }}</th>
                                <th class="border-top-0">{{ $t('attr.username') }}</th>
                                <th class="border-top-0">{{ $t('general.type') }}</th>
                                <th class="border-top-0">{{ $t('attr.plan') }}</th>
                                <th class="border-top-0 d-none d-lg-table-cell">{{ $t('attr.paymentMethod') }}</th>
                                <th class="border-top-0">{{ $t('ecommerce.product.price') }}</th>
                                <th class="border-top-0 d-none d-md-table-cell">{{ $t('attr.period') }}</th>
                                <th class="border-top-0 d-none d-md-table-cell">{{ $t('sentence.timeLeft') }}</th>
                                <th class="border-top-0 d-none d-md-table-cell">{{ $t('product.createdAt') }}</th>
                                <th class="border-top-0">{{ $t('main.status') }}</th>
                                <th class="border-top-0">{{ $t('main.action') }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="purchase in filteredPurchases" :key="purchase.id" class="product-row text-dark">
                                <td>
                                    <div class="product-cell">
                                        <img v-if="purchase.product?.pictures.length > 0" :src="storageUrl + purchase.product.pictures[0]"
                                            class="product-thumbnail" alt="Product" />
                                        <div class="product-info gap-2">
                                            <span class="product-name d-flex">
                                                {{ getProductName(purchase) }}
                                                <span :class="getStatusBadgeClass(purchase?.product?.status)">
                                                    {{ purchase?.product?.status }}
                                                </span>
                                            </span>
                                        </div>
                                    </div>
                                </td>
                                <td>
                                    <span class="font-weight-semibold">{{ getUserName(purchase) }}</span>
                                </td>
                                <td>
                                    <span class="font-weight-semibold" :class="getStatusBadgeClass(purchase.type)">{{
                                        $t(`boosterPlans.type.${purchase.type}`) }}</span>
                                </td>
                                <td>
                                    <span class="font-weight-semibold">{{ getPlanName(purchase) }}</span>
                                </td>
                                <td class="d-none d-lg-table-cell">
                                    <span class="text-muted">{{ getPaymentMethod(purchase) }}</span>
                                </td>
                                <td>
                                    <span class="font-weight-bold text-success">{{ formatPrice(purchase.amount_paid)
                                        }}</span>
                                </td>
                                <td class="d-none d-md-table-cell">
                                    <small class="text-muted">
                                        {{ formatDate(purchase.boosted_from) }}<br>
                                        {{ formatDate(purchase.boosted_until) }}
                                    </small>
                                </td>
                                <td class="d-none d-md-table-cell">
                                    <small class="text-muted">
                                        <TimerBooster :timeLeft="purchase.time_left" :isLong="false" />
                                    </small>
                                </td>
                                <td class="d-none d-md-table-cell">
                                    <small class="text-muted">
                                        {{ purchase.created_at | moment }}
                                    </small>
                                </td>
                                <td>
                                    <span :class="getStatusBadgeClass(purchase)" class="status-badge">
                                        {{ $t(`filter.${getComputedStatus(purchase)}`) }}
                                    </span>
                                </td>

                                <td>
                                    <div class="action-buttons-group">
                                        <button @click="viewDetails(purchase.id)"
                                            class="btn-action btn-view"
                                            :title="$t('action.view')">
                                            <i class="mdi mdi-eye"></i>
                                        </button>
                                        <button v-if="purchase.status === 'pending'" @click="openStatusModal(purchase)"
                                            class="btn-action btn-approve"
                                            :title="$t('action.updateStatus')">
                                            <i class="mdi mdi-list-status"></i>
                                        </button>
                                        <button v-if="purchase.status !== 'completed' && purchase.status !== 'cancelled'"
                                            @click="cancelPurchase(purchase.id)"
                                            class="btn-action btn-reject"
                                            :title="$t('action.cancel')">
                                            <i class="mdi mdi-cancel"></i>
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <!-- Empty State -->
        <div v-else class="admin-empty-state">
            <i class="mdi mdi-inbox"></i>
            <p class="admin-empty-text">{{ $t('sentence.noResults') }}</p>
        </div>

        <!-- Details Modal Backdrop -->
        <transition name="drawer-backdrop">
            <div v-if="showDetailsModal" class="admin-drawer-backdrop" @click="closeDetailsModal"></div>
        </transition>

        <!-- Details Modal -->
        <transition name="drawer">
            <div v-if="showDetailsModal" class="admin-drawer-container">
                <div class="admin-drawer-header">
                    <h3 class="admin-drawer-title">
                        {{ $t('attr.details') }}
                    </h3>
                    <button class="btn-close" @click="closeDetailsModal">
                        <i class="mdi mdi-close"></i>
                    </button>
                </div>

                <div class="admin-drawer-body" v-if="selectedPurchase">
                    <div class="detail-section">
                        <h5 class="section-title">{{ $t('main.information') }}</h5>
                        <div class="detail-row">
                            <span class="detail-label">{{ $t('#') }}:</span>
                            <span class="detail-value">#{{ selectedPurchase.id }}</span>
                        </div>
                        <div class="detail-row">
                            <span class="detail-label">{{ $t('attr.email') }}:</span>
                            <span class="detail-value">{{ getUserName(selectedPurchase) }}</span>
                        </div>
                        <div class="detail-row">
                            <span class="detail-label">{{ $t('attr.product') }}:</span>
                            <span class="detail-value">{{ getProductName(selectedPurchase) }}</span>
                        </div>
                        <div class="detail-row">
                            <span class="detail-label">{{ $t('main.status') }}:</span>
                            <span :class="getStatusBadgeClass(selectedPurchase.status)" class="status-badge">
                                {{ $t(`filter.${selectedPurchase.status}`) }}
                            </span>
                        </div>
                    </div>

                    <div class="detail-section">
                        <h5 class="section-title">{{ $t('attr.plan') }}</h5>
                        <div class="detail-row">
                            <span class="detail-label">{{ $t('attr.plan') }}:</span>
                            <span class="detail-value">{{ getPlanName(selectedPurchase) }}</span>
                        </div>
                        <div class="detail-row">
                            <span class="detail-label">{{ $t('ecommerce.product.price') }}:</span>
                            <span class="detail-value font-weight-bold text-success">
                                {{ formatPrice(selectedPurchase.amount_paid) }}
                            </span>
                        </div>
                        <div class="detail-row">
                            <span class="detail-label">{{ $t('attr.paymentMethod') }}:</span>
                            <span class="detail-value">{{ getPaymentMethod(selectedPurchase) }}</span>
                        </div>
                    </div>

                    <div class="detail-section">
                        <h5 class="section-title">{{ $t('attr.period') }}</h5>
                        <div class="detail-row">
                            <span class="detail-label">{{ $t('general.from') }}:</span>
                            <span class="detail-value">{{ formatDateLong(selectedPurchase.boosted_from) }}</span>
                        </div>
                        <div class="detail-row">
                            <span class="detail-label">{{ $t('general.until') }}:</span>
                            <span class="detail-value">{{ formatDateLong(selectedPurchase.boosted_until) }}</span>
                        </div>
                        <div class="detail-row">
                            <span class="detail-label">{{ $t('product.createdAt') }}:</span>
                            <span class="detail-value">{{ formatDateLong(selectedPurchase.created_at) }}</span>
                        </div>
                    </div>
                </div>

                <div class="admin-drawer-footer">
                    <button class="btn btn-secondary" @click="closeDetailsModal">
                        {{ $t('action.close') }}
                    </button>
                </div>
            </div>
        </transition>

        <!-- Status Update Modal Backdrop -->
        <transition name="drawer-backdrop">
            <div v-if="showStatusModal" class="admin-drawer-backdrop" @click="closeStatusModal"></div>
        </transition>

        <!-- Status Update Modal -->
        <transition name="drawer">
            <div v-if="showStatusModal" class="admin-drawer-container">
                <div class="admin-drawer-header">
                    <h3 class="admin-drawer-title">
                        {{ $t('boosterPurchase.updateStatus') }}
                    </h3>
                    <button class="btn-close" @click="closeStatusModal">
                        <i class="mdi mdi-close"></i>
                    </button>
                </div>

                <div class="admin-drawer-body">
                    <div class="admin-form-group">
                        <label class="admin-form-label">{{ $t('main.status') }} *</label>
                        <select v-model="statusForm.status" class="admin-form-input">
                            <option value="pending">{{ $t('filter.pending') }}</option>
                            <option value="active">{{ $t('filter.active') }}</option>
                        </select>
                    </div>
                </div>

                <div class="admin-drawer-footer">
                    <button class="btn btn-secondary" @click="closeStatusModal">
                        {{ $t('action.cancel') }}
                    </button>
                    <button class="btn btn-primary" @click="updateStatus">
                        {{ $t('action.update') }}
                    </button>
                </div>
            </div>
        </transition>

        <notifications style="top:5%;" width="35%" group="error" position="top center" />
        <notifications style="top:5%;" width="35%" group="success" position="top center" />
    </div>
</template>

<script>
import PageTitle from '@/components/templates/PageTitle.vue';
import LoadingSpinner from "@/components/templates/LoadingSpinner.vue";
import AlertService from '@/services/alertService';
import helper from "@/services/helper";
import TimerBooster from '@/components/templates/TimerBooster.vue';
import config from '@/config';

export default {
    components: { PageTitle, LoadingSpinner, TimerBooster },
    name: "BoosterPurchaseList",

    data() {
        return {
            purchases: [],
            storageUrl: config.storageUrl,
            showDetailsModal: false,
            showStatusModal: false,
            selectedPurchase: null,
            searchTerm: "",
            filterStatus: "",
            activeTab: "all",
            loader: false,
            statusForm: {
                status: ""
            }
        };
    },

    computed: {
        filteredPurchases() {
            let filtered=this.purchases;

            // Filter by type (tab)
            if (this.activeTab!=='all') {
                filtered=filtered.filter(p => p.type===this.activeTab);
            }

            // Filter by status using the computed status (handles expired/inProgress/completed etc.)
            if (this.filterStatus) {
                filtered=filtered.filter(p => this.getComputedStatus(p)===this.filterStatus);
            }

            // Filter by search term
            if (this.searchTerm) {
                const term=this.searchTerm.toLowerCase();
                filtered=filtered.filter(p =>
                    this.getUserName(p).toLowerCase().includes(term)||
                    this.getProductName(p).toLowerCase().includes(term)||
                    this.getPlanName(p).toLowerCase().includes(term)||
                    p.id.toString().includes(term)
                );
            }

            return filtered;
        }
    },

    mounted() {
        this.loadPurchases();
    },

    methods: {
        clearSearch() {
            this.searchTerm="";
        },

        formatPrice: helper.formatPrice,
        getStatusBadgeClass: helper.getStatusBadgeClass,
        formatDate(date) {
            if (!date) return '-';
            return new Date(date).toLocaleDateString();
        },

        formatDateLong(date) {
            if (!date) return '-';
            return new Date(date).toLocaleString();
        },

        getUserName(purchase) {
            return purchase.user?.username||purchase.user?.email||`User #${purchase.user_id}`;
        },

        getProductName(purchase) {
            return purchase.product?.title||purchase.product?.name||`Product #${purchase.product_id}`;
        },

        getPlanName(purchase) {
            return `${purchase.plan?.name} (J${purchase.plan?.duration_days})`||`Plan #${purchase.plan_id}`;
        },

        getPaymentMethod(purchase) {
            return purchase.payment_method?.name||purchase.payment_method?.type||`Method #${purchase.payment_method_id}`;
        },

        getComputedStatus: helper.getComputedStatus,

        async loadPurchases() {
            try {
                this.loader=true;
                const res=await this.$axios.get("/admin/booster-purchase");
                this.purchases=Array.isArray(res.data.data)
                    ? res.data.data
                    :Object.values(res.data.data);
                this.loader=false;
            } catch (err) {
                console.error("Error loading booster purchases:", err);
                this.$notify({
                    group: "error",
                    title: this.$t('sentence.errorLoading'),
                    type: "error",
                });
            }
        },

        async viewDetails(id) {
            try {
                const res=await this.$axios.get(`/admin/booster-purchase/${id}`);
                this.selectedPurchase=res.data.data||res.data;
                this.showDetailsModal=true;
            } catch (err) {
                console.error("Error loading purchase details:", err);
                this.$notify({
                    group: "error",
                    title: err.response?.data?.message||this.$t('sentence.errorLoading'),
                    type: "error",
                });
            }
        },

        closeDetailsModal() {
            this.showDetailsModal=false;
            this.selectedPurchase=null;
        },

        openStatusModal(purchase) {
            this.selectedPurchase=purchase;
            this.statusForm.status=purchase.status;
            this.showStatusModal=true;
        },

        closeStatusModal() {
            this.showStatusModal=false;
            this.selectedPurchase=null;
            this.statusForm.status="";
        },

        async updateStatus() {
            if (!this.selectedPurchase) return;

            try {
                const res=await this.$axios.put(
                    `/admin/booster-purchase/update-status/${this.selectedPurchase.id}`,
                    { status: this.statusForm.status }
                );
                this.$notify({
                    group: "success",
                    title: res.data.message||this.$t('sentence.updateSuccess'),
                    type: "success",
                });
                this.closeStatusModal();
                this.loadPurchases();
            } catch (err) {
                console.error("Error updating status:", err);
                this.$notify({
                    group: "error",
                    title: err.response?.data?.message||this.$t('sentence.errorSaving'),
                    type: "error",
                });
            }
        },

        async cancelPurchase(id) {
            const confirmed=await AlertService.confirm({
                title: this.$t('sentence.confirmCancel')||"Are you sure you want to cancel this purchase?",
                confirmButtonText: this.$t('action.confirm')||"Cancel Purchase",
                cancelButtonText: this.$t('action.cancel')||"Back",
                icon: "warning"
            });

            if (!confirmed) return;

            try {
                const res=await this.$axios.put(`/admin/booster-purchase/cancel/${id}`);
                this.$notify({
                    group: "success",
                    title: res.data.message||this.$t('sentence.cancelSuccess'),
                    type: "success",
                });
                this.loadPurchases();
            } catch (err) {
                console.error("Error cancelling purchase:", err);
                this.$notify({
                    group: "error",
                    title: err.response?.data?.message||this.$t('sentence.errorCancelling'),
                    type: "error",
                });
            }
        },

        async remove(id) {
            const confirmed=await AlertService.confirm({
                title: this.$t('sentence.confirmDelete')||"Are you sure?",
                confirmButtonText: this.$t('action.delete')||"Delete",
                cancelButtonText: this.$t('action.cancel')||"Cancel",
                icon: "warning"
            });

            if (!confirmed) return;

            try {
                const res=await this.$axios.delete(`/admin/booster-purchase/${id}`);
                this.$notify({
                    group: "success",
                    title: res.data.message||this.$t('sentence.deleteSuccess'),
                    type: "success",
                });
                this.loadPurchases();
            } catch (err) {
                console.error("Error deleting purchase:", err);
                this.$notify({
                    group: "error",
                    title: err.response?.data?.message||this.$t('sentence.errorDeleting'),
                    type: "error",
                });
            }
        }
    }
};
</script>


<style scoped src="@/assets/css/table-admin-styles.css"></style>